import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { IoIosSearch } from 'react-icons/io';
import Button from './Button';

/**
 * Search + segmented filter + result count (`.list-toolbar` in the spec).
 *
 * Two modes:
 * - URL mode (AccountList): `getFilterUrl` + `navigate` required. Same contract
 *   as components/SearchBox.js — strip regex metacharacters, submit on Enter
 *   via `navigate(getFilterUrl({ query }))`.
 * - Local mode (tab-scoped filter rows, e.g. AccountDetail's list tabs): pass
 *   `onSearch` instead. Filtering is client-side and derived, so every
 *   keystroke calls back immediately — there's nothing to "submit".
 */
function ListToolbar({
	query,
	getFilterUrl,
	navigate,
	onSearch,
	searchPlaceholder = 'Buscar',
	hideSearch = false,
	segments,
	activeSegment,
	onSegmentChange,
	segmentsLoading,
	chipFilter,
	onAdvancedFilter,
	filterBadgeCount,
	onClearFilters,
	resultCount,
}) {
	const [searchValue, setSearchValue] = useState(query && query !== 'all' ? query : '');
	const isLocalMode = Boolean(onSearch);

	const handleChange = (e) => {
		const value = e.target.value.replace(/[\\^$*+?()|[\]{}]/g, '');
		setSearchValue(value);
		if (isLocalMode) {
			onSearch(value);
		}
	};

	const submitSearch = () => {
		if (!isLocalMode && getFilterUrl && navigate) {
			navigate(getFilterUrl({ query: searchValue }));
		}
	};

	return (
		<div className='tw:flex tw:gap-2.5 tw:items-center tw:flex-wrap tw:mb-3.5'>
			{!hideSearch && (
				<div className='tw:flex tw:items-center tw:gap-2 tw:min-w-[320px] tw:h-[34px] tw:px-3 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:text-ink-2'>
					<IoIosSearch className='tw:text-[15px] tw:shrink-0' />
					<input
						type='text'
						className='entity-list-search-input tw:w-full tw:bg-transparent tw:border-0 tw:outline-none tw:text-[13px] tw:text-ink-0 placeholder:tw:text-ink-3'
						placeholder={searchPlaceholder}
						aria-label='buscar'
						value={searchValue}
						onChange={handleChange}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								submitSearch();
							}
						}}
					/>
				</div>
			)}

			{segmentsLoading ? (
				<span
					role='status'
					aria-label='Cargando filtros'
					aria-busy='true'
					className='tw:inline-flex tw:items-center tw:justify-center tw:h-[34px] tw:min-w-[180px] tw:px-3.5 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface'
				>
					<span className='el-dots' aria-hidden='true'>
						<i />
						<i />
						<i />
					</span>
				</span>
			) : (
				segments && segments.length > 0 && (
					<div className='tw:inline-flex tw:rounded-[7px] tw:border tw:border-border tw:overflow-hidden'>
						{segments.map((segment) => (
							<button
								key={segment.value}
								type='button'
								onClick={() => onSegmentChange(segment.value)}
								className={classnames(
									'tw:px-3 tw:py-1.5 tw:text-[12.5px] tw:font-medium tw:border-0',
									segment.value === activeSegment
										? 'tw:bg-accent tw:text-white'
										: 'tw:bg-surface tw:text-ink-2 tw:hover:bg-surface-2',
								)}
							>
								{segment.label}
							</button>
						))}
					</div>
				)
			)}

			{chipFilter && (
				<button
					type='button'
					onClick={chipFilter.onToggle}
					className={classnames(
						'tw:px-3 tw:py-1.5 tw:text-[12.5px] tw:font-medium tw:rounded-[7px] tw:border',
						chipFilter.active
							? 'tw:border-accent tw:text-accent tw:bg-accent-soft'
							: 'tw:border-border tw:text-ink-2 tw:bg-surface tw:hover:bg-surface-2',
					)}
				>
					{chipFilter.label}
				</button>
			)}

			{onAdvancedFilter && (
				<Button variant='ghost' size='sm' onClick={onAdvancedFilter}>
					Filtro avanzado
					{Boolean(filterBadgeCount) && (
						<span className='tw:ml-1.5 tw:inline-flex tw:items-center tw:justify-center tw:min-w-[16px] tw:h-4 tw:px-1 tw:rounded-full tw:bg-accent tw:text-white tw:text-[10px] tw:font-semibold'>
							{filterBadgeCount}
						</span>
					)}
				</Button>
			)}

			{onClearFilters && (
				<Button variant='ghost' size='sm' onClick={onClearFilters}>
					Limpiar
				</Button>
			)}

			{resultCount !== undefined && (
				<span className='tw:ml-auto tw:text-[12.5px] tw:text-ink-3'>
					{resultCount === null ? '—' : resultCount} {resultCount === 1 ? 'resultado' : 'resultados'}
				</span>
			)}
		</div>
	);
}

ListToolbar.propTypes = {
	query: PropTypes.string,
	getFilterUrl: PropTypes.func,
	navigate: PropTypes.func,
	onSearch: PropTypes.func,
	searchPlaceholder: PropTypes.string,
	// Toolbars whose data source has no text search (e.g. the admin history
	// viewer) render only segments + result count.
	hideSearch: PropTypes.bool,
	segments: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
		}),
	),
	activeSegment: PropTypes.string,
	onSegmentChange: PropTypes.func,
	// Replaces the segment group with a dots loader while its options are
	// still being fetched (e.g. concept lists in Debts/Credits). Static
	// segment lists never need this.
	segmentsLoading: PropTypes.bool,
	chipFilter: PropTypes.shape({
		label: PropTypes.string.isRequired,
		active: PropTypes.bool,
		onToggle: PropTypes.func.isRequired,
	}),
	onAdvancedFilter: PropTypes.func,
	filterBadgeCount: PropTypes.number,
	onClearFilters: PropTypes.func,
	// null renders "—" (count not known yet); undefined omits the count entirely.
	resultCount: PropTypes.number,
};

export default ListToolbar;
